import { defineStore } from "pinia"
import { useMachine } from "@xstate/vue"
import { useFormMachine } from "@machine/form.machine"
import { computed, inject, onBeforeMount, ref, watch } from "vue"
import { useRoute } from "vue-router"
import { Model, Services } from "@types"
import { useFetchWrapper } from "@composables"
import { TrackingPlugin } from "@plugins/tracking.plugin"

export const useFormStore = defineStore("form", () => {
    const route = useRoute()

    // =================================
    // States
    // =================================

    const {
        $getCSTBRecordById,
        $createCSTBRecord,
        $signSource,
        $signFostAndDpeConsent,
        $updateCSTBRecordUser,
        $patchBuilding,
        $deleteSource,
        $addSource,
        $sendFinalMail,
        $postFormProgress,
    } = inject<Services>("services")!

    const { $identify } = inject<TrackingPlugin>("tracking")!

    /**
     * Le document représentant le dossier CSTB
     */
    const cstbRecord = ref<Model["CSTBRecord"] | null>(null)

    onBeforeMount(async () => {
        identifyTrackingUser()
        await fetchCSTBRecord.fetch()

        if (fetchCSTBRecord.hasError.value) {
            // Supprime l'identifiant distinct de l'utilisateur dans le localStorage
            localStorage.removeItem("form_distinct_id")
            // Retire l'id de l'URL si il y en a un
            if (route.query._id_cstb_record) {
                const url = new URL(window.location.href)
                url.searchParams.delete("_id_cstb_record")
                window.history.replaceState({}, "", url.toString())
            }
        }

        const skipToStep =
            cstbRecord.value?.tracking.form_params?.skip_to_step || null

        send({
            type: "UPDATE_SKIP_TO_STEP",
            value: skipToStep,
        })

        send({ type: "REDIRECT" })
    })

    // =================================
    // Actions
    // =================================

    /**
     * Récupère le dossier CSTB à partir de l'ID dans l'URL
     */
    const fetchCSTBRecord = useFetchWrapper(async () => {
        // L'identifiant du lien d'invitation l'emporte sur celui mémorisé
        // dans le navigateur (DEV-2274) : un ménage qui ouvre SON lien sur un
        // poste où un autre dossier a été commencé retrouve le sien.
        const _id =
            route.query._id_cstb_record ||
            localStorage.getItem("form_distinct_id")

        if (_id) {
            cstbRecord.value = await $getCSTBRecordById(_id as string)
            // Met à jour le contexte de la machine
            send({
                type: "UPDATE_CONTEXT_FROM_API",
                data: cstbRecord.value,
            })

            // Met l'identifiant distinct de l'utilisateur dans le localStorage
            localStorage.setItem("form_distinct_id", _id as string)
        } else {
            localStorage.removeItem("form_distinct_id")
        }
    })

    /**
     * Crée un dossier CSTB
     */
    const fetchCreateCSTBRecord = useFetchWrapper(
        async ({
            username,
            firstname,
            lastname,
            phone,
            pdl_idsource,
            address,
        }: {
            username: string
            firstname: string
            lastname: string
            phone: string
            pdl_idsource: number
            address: object
        }) => {
            const payload = {
                username,
                firstname,
                lastname,
                phone,
                buildings: [
                    {
                        name: `Logement de ${firstname} ${lastname}`,
                        address: {
                            ...(address as object),
                            country: "France", // Ensure country is set to France
                        }, // Ensure address is of type object
                        sources: [
                            {
                                idtype: "pdl",
                                idsource: String(pdl_idsource), // Ensure idsource is a string
                            },
                        ],
                    },
                ] as [
                    {
                        name: string
                        address: object
                        sources: { idtype: string; idsource: string }[]
                    },
                ],
            }

            cstbRecord.value = await $createCSTBRecord(payload)
        }
    )

    /**
     * Crée une source d'un bâtiment d'un dossier CSTB
     */
    const fetchCreateSource = useFetchWrapper(
        async ({
            idtype,
            idsource,
            date_from_data,
        }: {
            idtype: string
            idsource: string
            date_from_data?: string
        }) => {
            if (!cstbRecord.value) return

            const { _id } = cstbRecord.value

            cstbRecord.value = await $addSource(
                _id,
                cstbRecord.value.buildings[0]._id,
                idtype,
                idsource,
                date_from_data
            )
        }
    )

    /**
     * Signer le consentement pour une source
     */
    const fetchSignConsent = useFetchWrapper(
        async ({
            idtype,
            idsource,
            consent_checked,
        }: {
            idtype: string
            idsource: string
            consent_checked?: boolean
        }) => {
            if (!cstbRecord.value) return

            const { _id } = cstbRecord.value

            cstbRecord.value = await $signSource(
                _id,
                cstbRecord.value.buildings[0]._id,
                idtype,
                idsource,
                { consent_checked }
            )
        }
    )

    /**
     * Supprime une source d'un bâtiment d'un dossier CSTB
     */
    const fetchDeleteSource = useFetchWrapper(
        async ({ idtype, idsource }: { idtype: string; idsource: string }) => {
            if (!cstbRecord.value) return

            const { _id } = cstbRecord.value

            cstbRecord.value = await $deleteSource(
                _id,
                cstbRecord.value.buildings[0]._id,
                idtype,
                idsource
            )
        }
    )

    /**
     * Met à jour les informations utilisateur du dossier CSTB
     */
    const fetchUpdateCSTBRecordUser = useFetchWrapper(
        async ({
            firstname,
            lastname,
            phone,
            username,
        }: {
            firstname: string
            lastname: string
            phone: string
            username: string
        }) => {
            if (!cstbRecord.value) return

            const { _id } = cstbRecord.value

            const payload: Partial<
                Record<"firstname" | "lastname" | "username" | "phone", string>
            > = {
                firstname,
                lastname,
                phone,
            }

            if (username) {
                payload["username"] = username
            }

            cstbRecord.value = await $updateCSTBRecordUser(_id, payload)
        }
    )

    /**
     * Met à jour les informations d'un bâtiment d'un dossier CSTB
     */
    const fetchUpdateCSTBRecordBuildingInfo = useFetchWrapper(
        async ({ info_logement, name, address }) => {
            if (!cstbRecord.value) return

            const { _id } = cstbRecord.value

            const payload: Record<string, any> = {}

            if (name) {
                payload["name"] = name
            }
            if (address) {
                payload["address"] = {
                    ...(address as object),
                    country: "France", // Ensure country is set to France
                } // Ensure address is of type object
            }
            if (info_logement) {
                payload["info_logement"] = info_logement
            }

            cstbRecord.value = await $patchBuilding(
                _id,
                cstbRecord.value.buildings[0]._id,
                payload
            )
        }
    )

    /**
     * Signe le consentement pour le dossier CSTB
     */
    const fetchSignFostAndDpeConsent = useFetchWrapper(
        async ({
            text,
            consent_checked,
        }: {
            text: string
            consent_checked?: boolean
        }) => {
            if (!cstbRecord.value) return

            const { _id } = cstbRecord.value

            cstbRecord.value = await $signFostAndDpeConsent(
                _id,
                cstbRecord.value.buildings[0]._id,
                { text, consent_checked }
            )
        }
    )

    /**
     * Envoie le mail de fin de dossier CSTB
     */
    const fetchSendFinalMail = useFetchWrapper(async () => {
        if (!cstbRecord.value) return

        const { _id } = cstbRecord.value

        await $sendFinalMail(_id)
    })

    // =================================
    // Getters
    // =================================

    /**
     * Dossiers créés via POST /user : saisie utilisateur désactivée, seul le numéro de compteur reste éditable.
     * Pris en charge aussi via query `disable_user_info=true` (liens dans les mails).
     */
    const isDisableUserInfo = computed(() => {
        if (route.query.disable_user_info === "true") return true
        if (!cstbRecord.value) return false
        return cstbRecord.value.tracking.form_params?.disable_user_info === true
    })
    /**
     * Indique si le dossier CSTB a été trouvé dans l'URL et si la recherche de PDL a été effectuée avec succès
     */
    const isFromUrlAndPdlFound = computed(() => {
        // Si l'ID du dossier CSTB n'est pas présent dans l'URL, on ne peut pas vérifier
        if (!route.query._id_cstb_record) return false

        if (!cstbRecord.value) return false

        const { tracking } = cstbRecord.value

        if (!tracking || !tracking.pdl_search) return false

        return !!tracking.pdl_search.find(
            (search) => search.status === "success"
        )
    })

    const isFromUrl = computed(() => {
        // Si l'ID du dossier CSTB n'est pas présent dans l'URL, on ne peut pas vérifier
        if (!route.query._id_cstb_record) return false

        if (!cstbRecord.value) return false

        return true
    })

    /**
     * Raccourci pour obtenir l'ID du premier bâtiment du dossier CSTB
     */
    const buildingId = computed(() => {
        if (!cstbRecord.value) return null
        if (!cstbRecord.value.buildings) return null
        if (cstbRecord.value.buildings.length === 0) return null
        return cstbRecord.value.buildings[0]._id
    })

    /**
     * Raccourci pour obtenir l'ID du dossier CSTB
     */
    const cstbRecordId = computed(() => {
        if (!cstbRecord.value) return null
        return cstbRecord.value._id
    })

    /**
     * Fonction utilitaire pour obtenir une source à partir de l'idtype
     * @param idtype - L'idtype de la source (pdl ou pce)
     * @returns
     */
    function getSourceByIdtype(idtype: string) {
        if (!cstbRecord.value) return null
        if (!cstbRecord.value.buildings) return null
        if (cstbRecord.value.buildings.length === 0) return null
        if (!cstbRecord.value.buildings[0].sources) return null
        if (cstbRecord.value.buildings[0].sources.length === 0) return null

        const source = cstbRecord.value.buildings[0].sources.find(
            (source) => source.idtype === idtype
        )
        if (!source) return null

        const consentSource =
            cstbRecord.value.buildings[0].consent?.consent_sources?.find(
                (s) => s._id_source === source._id_source && !s.revoked
            )

        return {
            ...source,
            _id_consent: consentSource?._id_consent,
            // Date de signature, si api-ww la renvoie (reçu, DEV-2274).
            consent_signed_at: consentSource?.signed_at ?? null,
        }
    }

    /**
     * Raccourci pour obtenir la source PDL
     */
    const pdl = computed(() => {
        return getSourceByIdtype("pdl") as {
            _id_source: string
            _id_consent: string
            idsource: string
            idtype: string
            consent_signed_at?: string | null
        }
    })

    /**
     * Raccourci pour obtenir la source PCE
     */
    const pce = computed(() => {
        return getSourceByIdtype("pce") as {
            _id_source: string
            _id_consent: string
            idsource: string
            idtype: string
            date_from_data: Date
            consent_signed_at?: string | null
        }
    })

    // =================================
    // Machines d'états (states)
    // mais initilasée après les actions
    // =================================

    const { machine } = useFormMachine({
        fetchCreateCSTBRecord,
        fetchSignConsent,
        fetchDeleteSource,
        fetchCreateSource,
        fetchUpdateCSTBRecordUser,
        fetchUpdateCSTBRecordBuildingInfo,
        fetchSignFostAndDpeConsent,
        identifyTrackingUser,
        buildingId,
        cstbRecord,
        pdl,
        pce,
    })

    const { send, snapshot } = useMachine(machine)

    // =================================
    // Balise de progression (DEV-1061) : à chaque changement d'état de la
    // machine, poste l'étape courante à l'API — la base sait enfin « a ouvert
    // le formulaire, s'est arrêté où ». Dédupliqué (même état = une balise),
    // fire-and-forget (le service avale les erreurs).
    // =================================
    let lastBeaconStep = ""
    watch(
        () => JSON.stringify(snapshot.value?.value ?? null),
        (raw) => {
            const _id = cstbRecordId.value
            if (!_id || !raw || raw === "null") return
            const step = String(raw).replace(/[{}"]/g, "").slice(0, 120)
            if (step === lastBeaconStep) return
            lastBeaconStep = step
            void $postFormProgress(_id, step)
        }
    )

    /**
     * Raccourci pour obtenir le snapshot de la machine en cours
     */
    const currentSnapshot = computed(() => snapshot)

    // =================================
    // Suivi du parcours
    // =================================

    function identifyTrackingUser() {
        const cstbRecordId =
            cstbRecord.value?._id || route.query._id_cstb_record

        if (!cstbRecordId) return

        $identify(cstbRecordId as string)
    }

    return {
        // States
        machine,
        send,
        cstbRecord,
        // Getters
        snapshot: currentSnapshot,
        cstbRecordId,
        buildingId,
        pdl,
        pce,
        isFromUrl,
        isFromUrlAndPdlFound,
        isDisableUserInfo,
        // Actions
        fetchCSTBRecord,
        fetchCreateCSTBRecord,
        fetchSignConsent,
        fetchUpdateCSTBRecordUser,
        fetchUpdateCSTBRecordBuildingInfo,
        fetchSignFostAndDpeConsent,
        fetchDeleteSource,
        fetchCreateSource,
        fetchSendFinalMail,

        // Suivi du parcours
        identifyTrackingUser,
    }
})
